import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {equalValidator} from '@services/user/validators/equal-validator.directive.service';
import {NotificationService} from '@services/notification/notification.service';

@Component({
    selector: 'scitis-password-reset',
    templateUrl: './password-reset.component.html',
    styleUrls: ['./password-reset.component.scss'],
    standalone: false
})
export class PasswordResetComponent implements OnInit, OnDestroy {

  public form: FormGroup = this.formBuilder.group({});
  public isPasswordVisible: boolean = false;

  private ngUnsubscribe: Subject<any> = new Subject<any>();
  // The user management action to be completed
  private mode: string = '';
  // Just a code Firebase uses to prove that this is a real password reset.
  private actionCode: string = '';
  private readonly passwordRegex = '^(?=.*\\d)(?=.*[a-zA-Z]).{8,}$';
  private readonly pageName = 'PasswordResetComponent';

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private firebaseAuthService: FirebaseAuthenticationService,
              private formBuilder: FormBuilder,
              private translate: TranslateService,
              private notificationService: NotificationService) {
    this.generateForm();
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(params => {
      // if we didn't receive any parameters,
      // we can't do anything
      if (!params) {
        const message = this.translate.instant('The used link is not valid, cannot process to reset the password.');
        this.notificationService.warning(message, this.pageName);
        this.navigateToSignInPage();
      }

      this.mode = params['mode'];
      this.actionCode = params['oobCode'];

      if (this.mode === 'resetPassword') {
        this.firebaseAuthService.verifyPasswordResetCode(this.actionCode).then(email => {
          const message = this.translate.instant('The reset link is valid. Password can be changed for $email').replace('$email', email);
          this.notificationService.success(message, this.pageName, false);
        }).catch(e => {
          // Invalid or expired action code. Ask user to try to reset the password again.
          const message = this.translate.instant('The reset link is invalid or expired. ');
          this.notificationService.error(message, e, this.pageName, true);
          this.navigateToSignInPage();
        });
      }


    });
  }

  ngOnDestroy() {
    // End all subscriptions listening to ngUnsubscribe
    // to avoid memory leaks.
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  /**
   * Attempt to confirm the password reset with firebase and
   * navigate user back to home.
   */
  handleResetPassword() {
    if (this.form.controls.password.value != this.form.controls.confirmPassword.value) {
      const message = this.translate.instant('New Password and Confirm Password do not match');
      this.notificationService.warning(message, this.pageName);
      return;
    }
    // Save the new password.
    this.firebaseAuthService.confirmPasswordReset(
        this.actionCode,
        this.form.controls.password.value
    )
    .then((_resp) => {
      const message = this.translate.instant('Password reset has been confirmed and new password updated. ');
      this.notificationService.success(message, this.pageName, true);
      this.navigateToSignInPage();
    }).catch(e => {
      const message = this.translate.instant('Error occurred during confirmation. The code might have expired or the password is too weak.');
      this.notificationService.error(message, e, this.pageName, true);
      this.navigateToSignInPage();
    });
  }

  public onShowPasswordChange() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  public redirectToLoginPage() {
    this.navigateToSignInPage();
  }

  private generateForm(): void {
    this.form = this.formBuilder.group({
      password: ['', [Validators.required, Validators.pattern(this.passwordRegex)]],
      confirmPassword: ['', Validators.required]
    });

    this.form.get('confirmPassword')?.setValidators([Validators.required, equalValidator(this.form.controls.password)]);
  }

  private navigateToSignInPage() {
    this.router.navigate(['/login']);
  }
}
